import { ApiData, Category, ItemOffer, Menu, Service, ServiceType } from "@/types"
import { MenuMode } from "@/types/menu-mode"

export type ServiceMenuData = {
  service: Service
  menu: Menu
  categories: Category[]
  itemNames: Record<string, string>
  itemOffers: ItemOffer[]
}

/**
 * Derives the menu data for a given service type from the raw API payload.
 *
 * Callers:
 * - DELIVERY → storefront catalog (also used for TAKEOUT **orders**; takeout
 *   does not get a separate menu in the app, only takeout **hours** differ).
 * - INDOOR → `?table=` mesa catalog (IndoorApiHydration).
 * - TAKEOUT → only used when you need the takeout **service** entity; prefer
 *   `findServiceByType(data, "TAKEOUT")` for hours. Do not hydrate the
 *   storefront from takeout's menuId.
 *
 * A size of a by-size product is not a menu entry, so its featured mark is carried by the product's entry.
 * Returns null when the service (or its menu) is absent from the payload.
 */
export function deriveServiceMenuData(
  data: ApiData,
  serviceType: ServiceType,
): ServiceMenuData | null {
  const service = data.services.find((s) => s.serviceType === serviceType)
  if (!service) return null

  const menu = data.menus.find((m) => m.id === service.menuId)
  if (!menu) return null

  if (!menu.menuMode) menu.menuMode = MenuMode.PRODUCT_LIST

  const categories = data.categories
    .filter((c) => menu.categoryId.includes(c.id))
    .sort((a, b) => a.index - b.index)

  const itemNames = data.items.reduce<Record<string, string>>(
    (acc, i) => ({ ...acc, [i.name]: i.id }),
    {},
  )

  const categoryItemOffersIds = categories.flatMap((c) => c.itemOfferId)
  const optionItemIds = data.optionGroups.flatMap((og) => og.options.map((opt) => opt.itemId))
  const optionItemOffersIds = data.itemOffers
    .filter((io) => optionItemIds.includes(io.itemId))
    .map((io) => io.id)
  const allItemOffersIdsSet = new Set([...categoryItemOffersIds, ...optionItemOffersIds])
  const menuEntryIds = new Set(categoryItemOffersIds)
  const featuredItemIds = new Set(data.itemOffers.filter((io) => io.isFeatured).map((io) => io.itemId))
  const itemOffers = data.itemOffers
    .filter((io) => allItemOffersIdsSet.has(io.id))
    .map((io) =>
      !io.isFeatured && menuEntryIds.has(io.id) && featuredItemIds.has(io.itemId) ? { ...io, isFeatured: true } : io,
    )

  return { service, menu, categories, itemNames, itemOffers }
}
